import apiService from "../../../../api/rest";
import { DEFAULT_TIMEZONE, isNumeric, roundOffByBankApproach } from "@/utils";

export const VENDOR_ALIAS_DEFAULT_SINGULAR = "Vendor";
export const VENDOR_ALIAS_DEFAULT_PLURAL = "Vendors";

export const getDefaultState = () => {
  return {
    venuesList: null,
    storedVenueInfo: null,
    wasDeepLinkedUsed: null,
    vendorAccessedDirectly: false
  };
};

// initial state
const state = getDefaultState();

// getters
const getters = {
  getVenueCollection(state) {
    return state.venuesList;
  },
  getVenueSettings(state, getters, rootState, rootGetters) {
    return rootGetters["venueSettings/getVenueSettings"];
  },
  getVenueIsAlcoholEnabled(state) {
    return state.storedVenueInfo?.alcoholEnabled;
  },
  getActiveVenue(state, getters) {
    return getters.getVenueSettings.venueId;
  },
  getActiveVenueUrl(state, getters) {
    return getters.getVenueSettings.url;
  },
  getActiveVenueByUrl(state, getters) {
    return function (url) {
      return url === getters.getActiveVenueUrl ? getters.getActiveVenue : isNumeric(url) ? parseInt(url, 10) : url;
    };
  },
  getVenueInfo(state) {
    return state.storedVenueInfo || {};
  },
  getVenueTimezone(state) {
    return state.storedVenueInfo?.timezone || DEFAULT_TIMEZONE;
  },
  getVendorAliasPlural(state, getters) {
    const pluralAlias = getters.getVenueSettings.pluralVendorName;
    return (typeof pluralAlias === "string" && pluralAlias.trim()) || VENDOR_ALIAS_DEFAULT_PLURAL;
  },
  getVendorAliasSingular(state, getters) {
    // SingularVerbiage is how it's coming from connect, should be changed to lowercase s.
    const singularAlias = getters.getVenueSettings.SingularVerbiage;
    return (typeof singularAlias === "string" && singularAlias.trim()) || VENDOR_ALIAS_DEFAULT_SINGULAR;
  },
  applyFee(state, getters) {
    return getters.getVenueInfo ? getters.getVenueInfo.fee === 1 : false;
  },
  feeRate(state, getters) {
    return getters.applyFee ? parseFloat(getters.getVenueInfo.convenienceFee) : 0;
  },
  getVenueFee(state, getters) {
    return (subTotal) => {
      const activeVenue = getters.getVenueInfo;
      return activeVenue.feeInPercent || subTotal === 0
        ? roundOffByBankApproach(subTotal * (getters.feeRate / 100))
        : getters.feeRate;
    };
  },
  isTaxPerItemEnabled(state, getters) {
    return getters.getVenueInfo ? getters.getVenueInfo.taxPerItem === 1 : false;
  },
  getVenueSpecialInstructionIsActive(state, getters) {
    return getters.getVenueSettings.displaySpecialInstructions === 1;
  },
  getWasDeepLinkedUsed(state) {
    return state.wasDeepLinkedUsed;
  },
  wasVendorAccessedDirectly(state) {
    return state.vendorAccessedDirectly;
  }
};

// actions
const actions = {
  fetchVenues({ commit, dispatch, getters }) {
    if (getters.getVenueCollection) {
      return Promise.resolve(getters.getVenueCollection);
    }

    // const fakeVenueId = 929;
    return dispatch("common/refreshToken", "", { root: true }).then(() => {
      return apiService.fetchVenues().then((response) => {
        if (response.data && response.data.venues) {
          commit("setVenuesList", response.data.venues);
        }
      });
    });
  },
  fetchActiveVenue({ getters, commit, dispatch, rootGetters }) {
    const savedVenue = getters.getVenueInfo.id && getters.getVenueInfo.id === getters.getActiveVenue;

    const callback = function () {
      return savedVenue ? Promise.resolve(true) : actions.updateActiveVenue({ getters, commit, dispatch, rootGetters });
    };

    return callback();
  },
  async updateActiveVenue({ getters, commit, dispatch, rootGetters }) {
    return apiService.fetchVenueById(getters.getActiveVenue).then(async (response) => {
      dispatch("common/updateVenueTimeStamp", null, { root: true });
      commit("setVenueInfo", response.data);
      if (rootGetters["venueSettings/seatRequired"] || getters.getWasDeepLinkedUsed) {
        return await dispatch("fetchVenueLevels");
      } else {
        return await dispatch("fetchVenueVendors");
      }
    });
  },
  fetchVenueLevels({ getters, dispatch }) {
    return dispatch("level/fetchVenueLevels", getters.getActiveVenue, {
      root: true
    }).then((venueLevels) => {
      if (Array.isArray(venueLevels) && venueLevels.length > 0) {
        return venueLevels;
      } else {
        return dispatch("fetchVenueVendors");
      }
    });
  },
  async fetchVenueVendors({ getters, dispatch }) {
    return await dispatch("vendor/fetchVenueVendors", getters.getActiveVenue, {
      root: true
    });
  }
};

// mutations
const mutations = {
  setVenueInfo(state, data) {
    state.storedVenueInfo = data;
  },
  setVenuesList(state, collection) {
    state.venuesList = collection;
  },
  setWasDeepLinkedUsed(state, data) {
    state.wasDeepLinkedUsed = data;
  },
  setVendorAccessedDirectly(state, data) {
    state.vendorAccessedDirectly = data;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
